import { useMemo, useState } from 'react';
import { sortCaseInsensitive, uniqueSorted } from './typesConfigUtils';

export type DeletedType = { type: string; originalPosition: number };

export type SortMode = 'alpha' | 'manual';

export function useMultiTypeReassignState(params: {
  text: string;
  sortMode: SortMode;
  setText: (next: string) => void;
  getTypesFromText: () => string[];
}) {
  const { text, sortMode, setText, getTypesFromText } = params;

  const [deletedTypes, setDeletedTypes] = useState<DeletedType[]>([]);
  const [reassignMap, setReassignMap] = useState<Record<string, string>>({});
  const [newTypeNames, setNewTypeNames] = useState<Record<string, string>>({});

  const availableReassignOptions = useMemo(() => {
    const currentTypes = getTypesFromText();
    const deletedSet = new Set(deletedTypes.map((d) => d.type));
    return currentTypes
      .filter((t) => !deletedSet.has(t))
      .sort(sortCaseInsensitive);
  }, [getTypesFromText, deletedTypes]);

  const closeSafeguard = () => {
    if (deletedTypes.length > 0) {
      const current = getTypesFromText();
      const currentSet = new Set(current);
      const toRestore = deletedTypes.filter((d) => !currentSet.has(d.type));
      if (toRestore.length > 0) {
        let next: string[];
        if (sortMode === 'alpha') {
          next = uniqueSorted([...current, ...toRestore.map((d) => d.type)]);
        } else {
          next = [...current];
          const sorted = [...toRestore].sort(
            (a, b) => a.originalPosition - b.originalPosition
          );
          for (const d of sorted) {
            const insertAt = Math.min(d.originalPosition, next.length);
            next.splice(insertAt >= 0 ? insertAt : next.length, 0, d.type);
          }
        }
        setText(next.join('\n'));
      }
    }
    setDeletedTypes([]);
    setReassignMap({});
    setNewTypeNames({});
  };

  return {
    deletedTypes,
    setDeletedTypes,
    reassignMap,
    setReassignMap,
    newTypeNames,
    setNewTypeNames,
    availableReassignOptions,
    closeSafeguard,
  };
}
